import {
  Box,
  Divider,
  FormControl,
  Grid,
  Input,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { platformUsersKeys } from "../../../api/queryKeys";
import RoleSelection from "../../../scenes/settings/account/RoleSelection";
import TitleTextAndSelection from "../../../scenes/settings/account/TitleTextAndSelection";
import {
  handleKeyDownForTextInput,
  isAdmin,
} from "../../../scenes/settings/account/utils";
import {
  MSSP_PLATFORM_ACCESS_ROLES,
  UserPlatformAccessRole,
  UserPlatformTitle,
} from "../../../utils/constants";
import PrimaryButton from "../../buttons/PrimaryButton";
import LoadingIcon from "../../LoadingIcon";
import { Toast } from "../../Toast";

const UserInvitationForm = ({ onUserInvited }) => {
  const colors = useColors();
  const { createPlatformUser, trackEvent } = useAPI();
  const queryClient = useQueryClient();

  const [userDetails, setUserDetails] = useState({
    title: "",
    accessRole: "",
    email: "",
    name: "",
    ownerships: [],
  });

  const [onSave, setOnSave] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState([]);

  const resetUserDetails = () => {
    setUserDetails({
      title: "",
      accessRole: "",
      email: "",
      name: "",
      ownerships: [],
    });
  };

  const handleTitleTypeSelection = (titleType) => {
    trackEvent(`User selected title type ${titleType}`);
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      title: titleType,
    }));
  };

  const handleAccessRoleSelection = (accessRole) => {
    trackEvent(`User selected role ${accessRole}`);
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      accessRole: accessRole,
    }));
  };

  const handleInvite = () => {
    setOnSave(true);
    const isUserAdmin = isAdmin(userDetails.accessRole);

    createPlatformUser(
      userDetails.email,
      userDetails.name,
      userDetails.title,
      userDetails.ownerships,
      isUserAdmin,
      userDetails.accessRole
    )
      .then((response) => {
        trackEvent(
          `User invite ${userDetails.email} to the platform during onboarding`
        );
        Toast.success("User invited successfully");
        queryClient.invalidateQueries({
          queryKey: platformUsersKeys.all,
        });

        const newUser = {
          name: userDetails.name,
          email: userDetails.email,
          title: userDetails.title,
          role: userDetails.accessRole,
        };

        setInvitedUsers([...invitedUsers, newUser]);
        resetUserDetails();

        if (onUserInvited) {
          onUserInvited(newUser);
        }
      })
      .catch((error) => {
        trackEvent(
          `User failed to invite ${userDetails.email} to the platform during onboarding`
        );
        if (error.response) {
          Toast.error(error.response.data);
        } else {
          Toast.error("Failed to invite user");
        }
      })
      .finally(() => {
        setOnSave(false);
      });
  };

  const areAllPropertiesFilled =
    userDetails.title?.length > 0 &&
    userDetails.accessRole?.length > 0 &&
    userDetails.email?.length > 0 &&
    userDetails.name?.length > 0;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            backgroundColor: colors.common_white,
            borderRadius: "10px",
            padding: "24px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Add New User
          </Typography>

          <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontSize={"14px"}>
              Full name *
            </Typography>
            <Input
              placeholder="Enter name"
              value={userDetails.name}
              onKeyDown={(event) => handleKeyDownForTextInput(event)}
              onChange={(event) =>
                setUserDetails((prevDetails) => ({
                  ...prevDetails,
                  name: event.target.value,
                }))
              }
              disableUnderline
              sx={{
                "& .MuiInput-input": {
                  borderBottom: `1px solid ${colors.grey_32percent}`,
                  fontSize: "12px",
                  color: colors.common_black,
                },
              }}
            />
          </FormControl>

          <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontSize={"14px"}>
              User email *
            </Typography>
            <Input
              placeholder="Enter email"
              value={userDetails.email}
              onChange={(event) =>
                setUserDetails((prevDetails) => ({
                  ...prevDetails,
                  email: event.target.value,
                }))
              }
              disableUnderline
              sx={{
                "& .MuiInput-input": {
                  borderBottom: `1px solid ${colors.grey_32percent}`,
                  fontSize: "12px",
                },
              }}
            />
          </FormControl>

          <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontSize={"14px"}>
              Select user title *
            </Typography>
            <TitleTextAndSelection
              selection={userDetails.title}
              placeholder="Select user title"
              setSelection={handleTitleTypeSelection}
              selectionList={UserPlatformTitle}
            />
          </FormControl>

          <FormControl variant="standard" fullWidth sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontSize={"14px"}>
              Select role type *
            </Typography>
            <RoleSelection
              selection={userDetails.accessRole}
              placeholder="Select role type"
              setSelection={handleAccessRoleSelection}
              selectionList={Object.values(UserPlatformAccessRole).filter(
                (role) => !MSSP_PLATFORM_ACCESS_ROLES.includes(role)
              )}
            />
          </FormControl>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {onSave ? (
              <LoadingIcon />
            ) : (
              <PrimaryButton
                onClick={handleInvite}
                disabled={!areAllPropertiesFilled || onSave}
                size="small"
              >
                Invite
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <Box
          sx={{
            backgroundColor: colors.common_white,
            borderRadius: "10px",
            padding: "24px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
            height: "100%",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Invited Users
          </Typography>

          {invitedUsers.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
                color: colors.grey_50percent,
              }}
            >
              <Typography>No users invited yet</Typography>
            </Box>
          ) : (
            <Box>
              {invitedUsers.map((user, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="subtitle1">{user.name}</Typography>
                    <Typography variant="body2" color={colors.grey_50percent}>
                      {user.role}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color={colors.grey_50percent}>
                    {user.email}
                  </Typography>
                  <Typography variant="body2" color={colors.grey_50percent}>
                    {user.title}
                  </Typography>
                  {index < invitedUsers.length - 1 && (
                    <Divider sx={{ mt: 1, mb: 1 }} />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default UserInvitationForm;

